import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  TextField,
  Card,
  CardContent,
  Stack,
  Link,
  IconButton,
  CircularProgress
} from '@mui/material';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import WarningIcon from '@mui/icons-material/Warning';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getDDQQuestionById, updateDDQAnswer, createDDQAnswer, getOrganizationUsers, updateDDQQuestion } from '../services/api';

const AIDrafts: React.FC = () => {
  const navigate = useNavigate();
  const { questionId } = useParams<{ questionId: string }>();
  const [currentQuestion, setCurrentQuestion] = useState<any>(null);
  const [nextQuestionId, setNextQuestionId] = useState<string | null>(null);
  const [prevQuestionId, setPrevQuestionId] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [answerText, setAnswerText] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [reviewerCandidates, setReviewerCandidates] = useState<any[]>([]);
  const [reviewerCandidatesLoading, setReviewerCandidatesLoading] = useState<boolean>(false);
  const [selectedReviewerId, setSelectedReviewerId] = useState<string | null>(null);
  const [isSendingForReview, setIsSendingForReview] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);


  useEffect(() => {
    const fetchQuestion = async () => {
      if (!questionId) {
        setError("No question ID provided.");
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const data = await getDDQQuestionById(questionId);
        setCurrentQuestion(data.question);
        setNextQuestionId(data.next_question_id);
        setPrevQuestionId(data.prev_question_id);
        setAnswerText(data.question.answers && data.question.answers.length > 0 ? data.question.answers[0].answer_text : '');
        setError(null);
      } catch (err) {
        setError('Failed to fetch question details.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchQuestion();
  }, [questionId]);

  const handleEdit = () => {
    setEditMode(true);
  };

  const handleSave = async () => {
    if (!currentQuestion) return;
    setSaving(true);
    try {
      if (currentQuestion.answers && currentQuestion.answers.length > 0) {
        const answerId = currentQuestion.answers[0].id;
        await updateDDQAnswer(answerId, answerText);
      } else {
        const newAnswer = await createDDQAnswer({
          answer_text: answerText,
          status: 'not_started',
          question_id: currentQuestion.id,
          sources: [],
          ai_generated: false,
        });
        setCurrentQuestion({
          ...currentQuestion,
          answers: [newAnswer],
        });
      }
      setEditMode(false);
    } catch (err) {
      console.error('Failed to save answer', err);
    } finally {
      setSaving(false);
    }
  };

  const handleReject = async () => {
    if (currentQuestion) {
      setIsRejecting(true);
      try {
        await updateDDQQuestion(
          currentQuestion.id,
          undefined,
          undefined,
          'rejected'
        );
        const data = await getDDQQuestionById(questionId!);
        setCurrentQuestion(data.question);
      } catch (err) {
        console.error('Failed to mark question as rejected', err);
      } finally {
        setIsRejecting(false);
      }
    }
  };

  const handleNextQuestion = () => {
    if (nextQuestionId) {
      navigate(`/ai-drafts/${nextQuestionId}`);
    }
  };

  const handlePreviousQuestion = () => {
    if (prevQuestionId) {
      navigate(`/ai-drafts/${prevQuestionId}`);
    }
  };

  const handleSendForReviewOpen = async () => {
    setAssignDialogOpen(true);
    const organization = localStorage.getItem('organization');
    if (organization) {
      const org = JSON.parse(organization);
      setReviewerCandidatesLoading(true);
      try {
        const orgUsersResponse = await getOrganizationUsers(org.id);
        const reviewers = orgUsersResponse.members.filter((orgUser: any) => orgUser.user.is_active && orgUser.role.toLowerCase() === 'reviewer');
        setReviewerCandidates(reviewers);
        setSelectedReviewerId(currentQuestion.reviewer?.id || null);
      } catch (err) {
        console.error('Failed to fetch organization users for reviewer assignment', err);
      } finally {
        setReviewerCandidatesLoading(false);
      }
    }
  };

  const handleAssignDialogClose = () => {
    setAssignDialogOpen(false);
  };

  const handleSendForReview = async () => {
    if (currentQuestion && selectedReviewerId) {
      setIsSendingForReview(true);
      try {
        await updateDDQQuestion(
          currentQuestion.id,
          undefined,
          selectedReviewerId,
          'needs_review'
        );
        const data = await getDDQQuestionById(questionId!);
        setCurrentQuestion(data.question);
      } catch (err) {
        console.error('Failed to update DDQ question', err);
      } finally {
        setIsSendingForReview(false);
      }
    }
    handleAssignDialogClose();
  };

  const handleMarkAsApproved = async () => {
    if (currentQuestion) {
      setIsApproving(true);
      try {
        await updateDDQQuestion(
          currentQuestion.id,
          undefined,
          undefined,
          'completed'
        );
        const data = await getDDQQuestionById(questionId!);
        setCurrentQuestion(data.question);
      } catch (err) {
        console.error('Failed to mark question as approved', err);
      } finally {
        setIsApproving(false);
      }
    }
  };


  const getStatusChip = (status: string) => {
      // completed = "completed"
      // in_progress = "in_progress"
      // needs_review = "needs_review"
      // not_started = "not_started"
    switch (status) {
      case 'approved':
      case 'completed':
        return <Chip icon={<CheckCircleIcon />} label="Approved" color="success" size="small" />;
      case 'review':
      case 'needs_review':
        return <Chip icon={<WarningIcon />} label="Needs Review" color="warning" size="small" />;
      case 'rejected':
        return <Chip icon={<WarningIcon />} label="Rejected" color="error" size="small" />;
      case 'in_progress':
        return <Chip label="Draft" variant="outlined" size="small" />;
      default:
        return <Chip label="Not Started" variant="outlined" size="small" />;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h5" color="error">{error}</Typography>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mt: 2 }}
        >
          Go Back
        </Button>
      </Box>
    );
  }

  if (!currentQuestion) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h5" color="text.secondary">Question not found.</Typography>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mt: 2 }}
        >
          Go Back
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton onClick={() => navigate(-1)} sx={{ mr: 1 }}>
                <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4">
              AI Answer Drafts
            </Typography>
        </Box>
        <Box>
          <Button
            startIcon={<NavigateBeforeIcon />}
            onClick={handlePreviousQuestion}
            disabled={!prevQuestionId}
            sx={{ mr: 1 }}
          >
            Previous
          </Button>
          <Button
            endIcon={<NavigateNextIcon />}
            onClick={handleNextQuestion}
            disabled={!nextQuestionId}
          >
            Next
          </Button>
        </Box>
      </Box>

      <Paper sx={{ mb: 3, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', height: 'calc(100vh - 250px)' }}>
          {/* Question Panel */}
          <Box sx={{ width: '30%', p: 3, borderRight: '1px solid rgba(0, 0, 0, 0.12)', overflowY: 'auto' }}>
            <Typography variant="h6" gutterBottom>
              QUESTION
            </Typography>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                DDQ — {currentQuestion.project?.name}
              </Typography>
              
              <Typography variant="h5" component="div" sx={{ my: 2 }}>
                {currentQuestion.question_text}
              </Typography>
              
              <Box sx={{ mt: 4 }}>
                <Typography variant="body2" gutterBottom>
                  Confidence score: <strong>{currentQuestion.answers?.[0]?.confidence_score || 'N/A'}</strong>
                </Typography>
                
                <Typography variant="body2" gutterBottom>
                  Based on:
                </Typography>
                
                <Stack spacing={1} sx={{ mt: 1 }}>
                  {currentQuestion.answers?.[0]?.sources?.map((source: any, index: number) => (
                    <Box key={index}>
                      <Link href="#" underline="hover">
                        {source.name}
                      </Link>
                      <Chip
                        label={source.type}
                        size="small"
                        sx={{ ml: 1, fontSize: '0.7rem' }}
                        variant="outlined"
                      />
                    </Box>
                  ))}
                </Stack>
              </Box>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" gutterBottom>
                  Sources:
                </Typography>
                <Stack spacing={1} sx={{ mt: 1, maxHeight: '150px', overflow: 'auto' }}>
                  {currentQuestion.sources?.map((source: any) => (
                    <Card key={source.id} variant="outlined">
                      <CardContent sx={{ p: '12px !important' }}>
                        <Typography sx={{ fontSize: 14 }} color="text.secondary" gutterBottom>
                          P. {source.page_number} &mdash; {source.section_name}
                        </Typography>
                        <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                          "{source.surrounding_text}"
                        </Typography>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              </Box>
            </Box>
          </Box>
          
          {/* AI Suggested Answer Panel */}
          <Box sx={{ width: '40%', p: 3, borderRight: '1px solid rgba(0, 0, 0, 0.12)', overflowY: 'auto' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                AI SUGGESTED ANSWER
              </Typography>
              
              <Chip
                label={currentQuestion.answers?.[0]?.confidence_score || 'N/A'}
                size="small"
                color={currentQuestion.answers?.[0]?.confidence_score > 90 ? "success" : "default"}
              />
            </Box>
            
            {editMode ? (
              <Box>
                <TextField
                  multiline
                  fullWidth
                  minRows={10}
                  maxRows={15}
                  value={answerText}
                  onChange={(e) => setAnswerText(e.target.value)}
                  variant="outlined"
                  sx={{ mb: 2 }}
                />
                
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => setEditMode(false)}
                  >
                    Cancel
                  </Button>
                  
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? <CircularProgress size={24} /> : 'Save'}
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    minHeight: '300px',
                    maxHeight: 'calc(100vh - 400px)',
                    overflow: 'auto'
                  }}
                >
                  <Typography variant="body1">
                    {answerText}
                  </Typography>
                </Paper>
                
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3 }}>
                  {/* <Button
                    variant="contained"
                    color="primary"
                    onClick={handleUseAsIs}
                  >
                    Use as-is
                  </Button> */}
                  
                  <Button
                    variant="outlined"
                    onClick={handleEdit}
                  >
                    Edit
                  </Button>
                  
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleReject}
                    disabled={isRejecting}
                  >
                    {isRejecting ? <CircularProgress size={24} /> : 'Reject'}
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
          
          {/* Matching Past Answers Panel */}
          <Box sx={{ width: '30%', p: 3, overflowY: 'auto' }}>
            <Typography variant="h6" gutterBottom>
              MATCHING PAST ANSWERS
            </Typography>
            
            <Stack spacing={2} sx={{ mt: 2, maxHeight: 'calc(100vh - 350px)', overflow: 'auto' }}>
              {currentQuestion.answers?.slice(1).map((answer: any) => (
                <Card key={answer.id} variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                      {answer.source || 'Past Answer'}
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary">
                      {answer.text}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                      <Button
                        size="small"
                        endIcon={<VisibilityIcon />}
                        onClick={() => alert(answer.fullText)}
                      >
                        View full answer
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </Box>
        </Box>
      </Paper>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          {getStatusChip(currentQuestion.status)}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Last updated • {new Date(currentQuestion.updated_at).toLocaleString()}
          </Typography>
        </Box>
        
        <Box>
          <Button
            variant="contained"
            color="primary"
            sx={{ mr: 2 }}
            onClick={handleMarkAsApproved}
            disabled={isApproving}
          >
            {isApproving ? <CircularProgress size={24} /> : 'Mark as Approved'}
          </Button>
          <Button variant="outlined" onClick={handleSendForReviewOpen}>
            Send for Review
          </Button>
        </Box>
      </Box>
      <Dialog open={assignDialogOpen} onClose={handleAssignDialogClose}>
        <DialogTitle>Assign Reviewer</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2" gutterBottom>
            Question:
          </Typography>
          <Typography variant="body2" paragraph>
            {currentQuestion?.question_text}
          </Typography>

          <FormControl fullWidth sx={{ mt: 2 }} variant="outlined">
            <InputLabel id="reviewer-select-label" shrink>
              Reviewer
            </InputLabel>
            <Select
              labelId="reviewer-select-label"
              label="Reviewer"
              value={selectedReviewerId || ''}
              onChange={(e) => setSelectedReviewerId(e.target.value as string)}
              displayEmpty
            >
              <MenuItem value="">
                Not assigned
              </MenuItem>
              {reviewerCandidatesLoading ? (
                <MenuItem disabled>
                  <CircularProgress size={20} />
                  <Typography sx={{ ml: 1 }}>Loading users...</Typography>
                </MenuItem>
              ) : (
                reviewerCandidates.map((member) => (
                  <MenuItem key={member.user.id} value={member.user.id}>
                    {member.user.name || member.user.email.split('@')[0]} ({member.role})
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAssignDialogClose}>Cancel</Button>
          <Button
            onClick={handleSendForReview}
            variant="contained"
            disabled={isSendingForReview}
          >
            {isSendingForReview ? <CircularProgress size={24} /> : 'Send for Review'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AIDrafts;